/**
 * @file DashboardPage.js
 * @description Main page used to display Dashboards which return data for the specific client for Client Admin / Multi Tenant Admin users.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    getDashboardAllData,
    getDashboardData,
} from '../../services/dashboardService';
import { fetchPopulationGroups } from '../../services/populationGroupService';
import { getClient } from '../../services/clientService';
import { SearchableDropdown } from '../helpers/SearchableDropdown';
import TextRangeDatePicker from '../helpers/TextRangeDatePicker';
import SideDrawer from '../helpers/SideDrawer';
import Spinner from '../ui/spinners/Spinner';
import Toast from '../ui/toasts/Toast';
import { LogEventType, tenantTierLabels } from '../../utils/data/enums';
import { logEvent } from '../../services/loggingService';
import {
    DASHBOARD_POPULATION_GROUPS_MAX,
    DASHBOARD_REPORT_DEFINITIONS_SECTIONS,
    DASHBOARD_TOOLTIP_MESSAGE_TEXT,
    DASHBOARD_DEFAULT_START_DATE,
    DASHBOARD_DEFAULT_END_DATE,
    DASHBOARD_DEFAULT_SPINNER_WIDTH,
    DASHBOARD_DEFAULT_DATA,
    DASHBOARD_DEFAULT_LOADING_STATE,
    DASHBOARD_DEFAULT_POPULATION_GROUP_NAMES,
    buildDashboardLoadingState,
    mapDashboardResponseToCardData,
    getDrillDownDefinitionHeader,
    getDrillDownDefinitionSections,
    exportDashboardAllCardsCsvZip,
    exportDashboardCardCsv,
    exportDashboardReportPdf,
} from './DashboardUtils';
import { DashboardTypeEnum } from '../../utils/data/enums';
import { useAuthContext } from '../../store/Contexts';
import { useAdminAudienceEnabled } from '../../hooks/useAdminAudienceEnabled';
import CardContainer from './CardContainer';
import Registration from './Registration';
import EmploymentStatus from './EmploymentStatus';
import SessionsByDevice from './SessionsByDevice';
import SessionsDaily from './SessionsDaily';
import MostVisitedPages from './MostVisitedPages';
import MostClickedContent from './MostClickedContent';
import MessagesUserPreference from './MessagesUserPreference';
import MessagesSent from './MessagesSent';
import DashboardDrillDownView, {
    getDrillDownLabels,
} from './DashboardDrillDownView';
import AudienceBadge from '../shared/AudienceBadge';
import './DashboardPage.scss';
import TenantTierBadge from '../ui/svg/TenantTierBadge';
import { getTenantTierLabel } from '../../utils/utils';
import { getText } from '../../i18n';

const DASHBOARD_TYPES = Object.values(DashboardTypeEnum);

const DASHBOARD_RESPONSE_FIELD_BY_TYPE = {
    [DashboardTypeEnum.Registration]: 'registration',
    [DashboardTypeEnum.EmploymentStatus]: 'employmentStatus',
    [DashboardTypeEnum.SessionsByDevice]: 'sessionsByDevice',
    [DashboardTypeEnum.SessionsDaily]: 'sessionsDaily',
    [DashboardTypeEnum.MostVisitedPages]: 'mostVisitedPages',
    [DashboardTypeEnum.MostClickedContent]: 'mostClickedContent',
    [DashboardTypeEnum.MessagesUserPreference]: 'messagesUserPreference',
    [DashboardTypeEnum.MessagesSent]: 'messagesSent',
};

const DRILL_DOWN_SEARCH_KEY = 'drillDown';

const DRILL_DOWN_SEARCH_VALUE_TO_TYPE = Object.freeze({
    'most-visited-pages': DashboardTypeEnum.MostVisitedPages,
    'most-clicked-content': DashboardTypeEnum.MostClickedContent,
});

const DRILL_DOWN_TYPE_TO_SEARCH_VALUE = Object.freeze({
    [DashboardTypeEnum.MostVisitedPages]: 'most-visited-pages',
    [DashboardTypeEnum.MostClickedContent]: 'most-clicked-content',
});

const DRILL_DOWN_QUERY_DEFAULTS_BY_TYPE = Object.freeze({
    [DashboardTypeEnum.MostVisitedPages]: {
        page: 1,
        pageSize: 20,
        sortBy: 'views',
        sortOrder: 'desc',
    },
    [DashboardTypeEnum.MostClickedContent]: {
        page: 1,
        pageSize: 20,
        sortBy: 'clicks',
        sortOrder: 'desc',
    },
});

const getDrillDownQueryDefaults = dashboardTypeId => {
    return (
        DRILL_DOWN_QUERY_DEFAULTS_BY_TYPE[dashboardTypeId] || {
            page: 1,
            pageSize: 20,
            sortBy: 'views',
            sortOrder: 'desc',
        }
    );
};

const DashboardPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { user } = useAuthContext();
    const { isAdminAudienceEnabled } = useAdminAudienceEnabled();
    const [startDate, setStartDate] = useState(DASHBOARD_DEFAULT_START_DATE);
    const [endDate, setEndDate] = useState(DASHBOARD_DEFAULT_END_DATE);
    const [drillDownStartDate, setDrillDownStartDate] = useState(
        DASHBOARD_DEFAULT_START_DATE,
    );
    const [drillDownEndDate, setDrillDownEndDate] = useState(
        DASHBOARD_DEFAULT_END_DATE,
    );
    const [populationGroupState, setPopulationGroupState] = useState({
        groups: [],
        selected: [],
        hasInitialized: false,
    });
    const [drillDownPopulationGroupState, setDrillDownPopulationGroupState] =
        useState({
            groups: [],
            selected: [],
            hasInitialized: false,
        });
    const [showAllPopulationGroups, setShowAllPopulationGroups] =
        useState(false);
    const [
        showAllDrillDownPopulationGroups,
        setShowAllDrillDownPopulationGroups,
    ] = useState(false);
    const [dashboardData, setDashboardData] = useState(DASHBOARD_DEFAULT_DATA);
    const [loadingState, setLoadingState] = useState(
        DASHBOARD_DEFAULT_LOADING_STATE,
    );
    const [isExportingReport, setIsExportingReport] = useState(false);
    const [isDownloadMenuOpen, setIsDownloadMenuOpen] = useState(false);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [isDrillDownLoading, setIsDrillDownLoading] = useState(false);
    const [drillDownResetVersion, setDrillDownResetVersion] = useState(0);
    const [drillDownData, setDrillDownData] = useState(null);
    const tenantId = window.localStorage.getItem('tenantId');
    const [clientName, setClientName] = useState('');
    const [companyCode, setCompanyCode] = useState('');
    const [tenantTierId, setTenantTierId] = useState(null);
    const pageContentRef = useRef(null);
    const downloadMenuRef = useRef(null);
    const drillDownQueryOptionsByTypeRef = useRef({});
    const drillDownRowsForExportRef = useRef([]);
    const getCompanyCode = () =>
        window.localStorage.getItem('companyCode') || '';
    const activeDrillDownType =
        DRILL_DOWN_SEARCH_VALUE_TO_TYPE[
            searchParams.get(DRILL_DOWN_SEARCH_KEY)
        ] || null;
    const activePopulationGroupState = activeDrillDownType
        ? drillDownPopulationGroupState
        : populationGroupState;
    const showAllActivePopulationGroups = activeDrillDownType
        ? showAllDrillDownPopulationGroups
        : showAllPopulationGroups;
    const dashboardPageClassName = `dashboard-page${
        isExportingReport ? ' exporting-report' : ''
    }${activeDrillDownType ? ' has-drill-down' : ''}`;
    const hasViewerDataFilter =
        isAdminAudienceEnabled && Boolean(user?.jwt?.df);
    const viewerAudienceGroup = activePopulationGroupState.groups.find(
        group => group.id === user?.jwt?.df,
    );
    const viewerAudienceLabel =
        viewerAudienceGroup?.name || String(user?.jwt?.df || '');
    const drawerHeader = activeDrillDownType
        ? getDrillDownDefinitionHeader(activeDrillDownType)
        : 'Terms and Definitions';
    const drawerSections = activeDrillDownType
        ? getDrillDownDefinitionSections(activeDrillDownType)
        : DASHBOARD_REPORT_DEFINITIONS_SECTIONS;

    // Load population groups on initial page render, as well as the client name.
    useEffect(() => {
        const loadPopGroups = async () => {
            try {
                const fetchedPopGroups = await fetchPopulationGroups(null);
                const allIndividualsGroup = fetchedPopGroups.find(
                    group => group.name === 'All Individuals',
                );
                const groupsWithChecked = fetchedPopGroups.map(group => ({
                    ...group,
                    checked: group.id === allIndividualsGroup?.id,
                }));

                // Default population groups to Statuses (Active, Leave of Absence, Terminated)
                const defaultStatusPopulationGroups = fetchedPopGroups
                    .filter(group =>
                        DASHBOARD_DEFAULT_POPULATION_GROUP_NAMES.includes(
                            group.name,
                        ),
                    )
                    .map(group => ({
                        ...group,
                        checked: group.id === allIndividualsGroup?.id,
                    }));
                setPopulationGroupState({
                    groups: groupsWithChecked,
                    selected: defaultStatusPopulationGroups ?? [],
                    hasInitialized: true,
                });
                setDrillDownPopulationGroupState({
                    groups: groupsWithChecked.map(group => ({ ...group })),
                    selected: (defaultStatusPopulationGroups ?? []).map(
                        group => ({ ...group }),
                    ),
                    hasInitialized: true,
                });
            } catch (error) {
                Toast.error(
                    getText('msg_e5df9e5e8f12'),
                );
            }
        };
        const loadClient = async () => {
            try {
                const client = await getClient(tenantId);
                setClientName(client?.name);
                setTenantTierId(client?.tenantTierId ?? null);
                const code = getCompanyCode();
                setCompanyCode(code);
            } catch (error) {
                Toast.error(getText('msg_efdb7ad1181e'));
            }
        };
        loadPopGroups();
        loadClient();
    }, [tenantId]);

    useEffect(() => {
        const handleOutsideClick = event => {
            if (!downloadMenuRef.current?.contains(event.target)) {
                setIsDownloadMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleOutsideClick);

        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
        };
    }, []);

    const updatePopulationGroupsState = (setPopulationState, newSelection) => {
        setPopulationState(prev => {
            const updatedGroups = prev.groups.map(g => ({
                ...g,
                checked: newSelection.some(sel => sel.id === g.id),
            }));
            const sortedGroups = updatedGroups.sort((a, b) => {
                const aChecked = newSelection.some(sel => sel.id === a.id);
                const bChecked = newSelection.some(sel => sel.id === b.id);
                if (aChecked && !bChecked) return -1;
                if (!aChecked && bChecked) return 1;
                return a.name.localeCompare(b.name);
            });
            return {
                ...prev,
                groups: sortedGroups,
                selected: newSelection,
            };
        });
    };

    // Update population group state and sort on selection.
    const updatePopulationGroups = newSelection => {
        updatePopulationGroupsState(
            activeDrillDownType
                ? setDrillDownPopulationGroupState
                : setPopulationGroupState,
            newSelection,
        );
    };

    const handleRemovePopulationGroup = populationGroup => {
        if (activePopulationGroupState.selected.length > 1) {
            const setPopulationState = activeDrillDownType
                ? setDrillDownPopulationGroupState
                : setPopulationGroupState;

            setPopulationState(prevState => {
                const updatedGroups = prevState.groups.map(group =>
                    group.id === populationGroup.id
                        ? { ...group, checked: false }
                        : group,
                );

                const updatedSelected = prevState.selected.filter(
                    group => group.id !== populationGroup.id,
                );

                return {
                    ...prevState,
                    groups: updatedGroups,
                    selected: updatedSelected,
                };
            });
        }
    };

    const loadDashboardData = useCallback(async () => {
        setLoadingState(buildDashboardLoadingState(DASHBOARD_TYPES, true));
        try {
            const allDashboardResponse = await getDashboardAllData(
                tenantId,
                startDate,
                endDate,
                populationGroupState.selected.map(group => group.id),
            );
            const mappedDashboardData = mapDashboardResponseToCardData(
                DASHBOARD_TYPES,
                DASHBOARD_RESPONSE_FIELD_BY_TYPE,
                allDashboardResponse,
                DASHBOARD_DEFAULT_DATA,
            );
            setDashboardData(mappedDashboardData);
        } catch (error) {
            setDashboardData(DASHBOARD_DEFAULT_DATA);
            Toast.error(getText('msg_1b7685d77c37'));
        }
        setLoadingState(buildDashboardLoadingState(DASHBOARD_TYPES, false));
    }, [tenantId, startDate, endDate, populationGroupState.selected]);

    // Fetch the dashboard data anytime the user changes the startDate, endDate, or populationGroup selection.
    useEffect(() => {
        const noPopulationGroups =
            !populationGroupState.hasInitialized &&
            populationGroupState.selected.length === 0;
        if (noPopulationGroups) return;
        loadDashboardData();
    }, [startDate, endDate, populationGroupState, loadDashboardData]);

    // Report download method to download a snapshot of the Dashboard page.
    const handleDownloadReport = async () => {
        if (isExportingReport) {
            return;
        }

        setIsExportingReport(true);
        setShowAllPopulationGroups(true);
        try {
            await exportDashboardReportPdf();
            logEvent(LogEventType.DataDownloaded, `Dashboard data downloaded`);
        } catch (error) {
            Toast.error(getText('msg_fd5b354e8798'));
        } finally {
            if (activeDrillDownType) {
                setShowAllDrillDownPopulationGroups(false);
            } else {
                setShowAllPopulationGroups(false);
            }
            setIsExportingReport(false);
        }
    };

    const handleDownloadAllDashboardCsv = async () => {
        if (isExportingReport) {
            return;
        }

        setIsDownloadMenuOpen(false);

        if (activeDrillDownType) {
            try {
                await exportDashboardCardCsv(
                    activeDrillDownType,
                    drillDownRowsForExportRef.current,
                );
                logEvent(
                    LogEventType.DataDownloaded,
                    `Dashboard drill-down CSV data downloaded`,
                );
            } catch (error) {
                Toast.error(getText('msg_f6c8080d9637'));
            }

            return;
        }

        setShowAllPopulationGroups(true);
        try {
            await exportDashboardAllCardsCsvZip(dashboardData);
            logEvent(
                LogEventType.DataDownloaded,
                `Dashboard CSV data downloaded`,
            );
        } catch (error) {
            Toast.error(getText('msg_a77526eed3b7'));
        } finally {
            if (activeDrillDownType) {
                setShowAllDrillDownPopulationGroups(false);
            } else {
                setShowAllPopulationGroups(false);
            }
        }
    };

    const handleDownloadPdfReport = async () => {
        setIsDownloadMenuOpen(false);
        await handleDownloadReport();
    };

    const handleToggleDownloadMenu = () => {
        setIsDownloadMenuOpen(current => !current);
    };

    const handleDownloadDashboardCardCsv = async dashboardTypeId => {
        try {
            await exportDashboardCardCsv(
                dashboardTypeId,
                dashboardData[dashboardTypeId],
            );
        } catch (error) {
            Toast.error(getText('msg_f6c8080d9637'));
        }
    };

    const scrollDashboardToTop = () => {
        if (pageContentRef.current) {
            pageContentRef.current.scrollTo({ top: 0, left: 0 });
        }

        window.scrollTo({ top: 0, left: 0 });
    };

    const handleOpenDrillDown = dashboardTypeId => {
        const drillDownSearchValue =
            DRILL_DOWN_TYPE_TO_SEARCH_VALUE[dashboardTypeId];
        if (!drillDownSearchValue) {
            return;
        }

        const updatedSearchParams = new URLSearchParams(searchParams);
        updatedSearchParams.set(DRILL_DOWN_SEARCH_KEY, drillDownSearchValue);

        drillDownQueryOptionsByTypeRef.current[dashboardTypeId] =
            getDrillDownQueryDefaults(dashboardTypeId);

        setDrillDownStartDate(startDate ? new Date(startDate) : startDate);
        setDrillDownEndDate(endDate ? new Date(endDate) : endDate);
        setDrillDownPopulationGroupState({
            groups: populationGroupState.groups.map(group => ({ ...group })),
            selected: populationGroupState.selected.map(group => ({
                ...group,
            })),
            hasInitialized: populationGroupState.hasInitialized,
        });
        setShowAllDrillDownPopulationGroups(false);

        setSearchParams(updatedSearchParams);
        window.requestAnimationFrame(scrollDashboardToTop);
    };

    const handleCloseDrillDown = () => {
        setIsDrillDownLoading(false);
        drillDownRowsForExportRef.current = [];
        const updatedSearchParams = new URLSearchParams(searchParams);
        updatedSearchParams.delete(DRILL_DOWN_SEARCH_KEY);
        setSearchParams(updatedSearchParams);
    };

    const handleDrillDownQueryChange = useCallback(
        async (dashboardTypeId, queryOptions) => {
            const nextQueryOptions = {
                ...getDrillDownQueryDefaults(dashboardTypeId),
                ...(queryOptions || {}),
            };

            drillDownQueryOptionsByTypeRef.current[dashboardTypeId] =
                nextQueryOptions;

            try {
                setIsDrillDownLoading(true);
                const result = await getDashboardData(
                    tenantId,
                    drillDownStartDate,
                    drillDownEndDate,
                    drillDownPopulationGroupState.selected.map(
                        group => group.id,
                    ),
                    dashboardTypeId,
                    nextQueryOptions,
                );
                setDrillDownData(result);
            } catch (error) {
                setDrillDownData(null);
                console.error('Error fetching drill-down data:', error);
                Toast.error(getText('msg_934ec9f74d2a'));
            } finally {
                setIsDrillDownLoading(false);
            }
        },
        [
            tenantId,
            drillDownStartDate,
            drillDownEndDate,
            drillDownPopulationGroupState.selected,
        ],
    );

    const handleDrillDownRowsForExportChange = useCallback(rows => {
        drillDownRowsForExportRef.current = Array.isArray(rows) ? rows : [];
    }, []);

    const handleClearDrillDownFilters = useCallback(() => {
        if (!activeDrillDownType) {
            return;
        }

        const defaultQueryOptions =
            getDrillDownQueryDefaults(activeDrillDownType);
        drillDownQueryOptionsByTypeRef.current[activeDrillDownType] =
            defaultQueryOptions;

        setDrillDownResetVersion(currentVersion => currentVersion + 1);
        handleDrillDownQueryChange(activeDrillDownType, defaultQueryOptions);
    }, [activeDrillDownType, handleDrillDownQueryChange]);

    useEffect(() => {
        if (!activeDrillDownType) {
            drillDownRowsForExportRef.current = [];
            return;
        }

        const noPopulationGroups =
            !drillDownPopulationGroupState.hasInitialized &&
            drillDownPopulationGroupState.selected.length === 0;

        if (noPopulationGroups) {
            return;
        }

        const queryOptions =
            drillDownQueryOptionsByTypeRef.current[activeDrillDownType] ||
            getDrillDownQueryDefaults(activeDrillDownType);

        handleDrillDownQueryChange(activeDrillDownType, queryOptions);
    }, [
        activeDrillDownType,
        drillDownStartDate,
        drillDownEndDate,
        drillDownPopulationGroupState.hasInitialized,
        drillDownPopulationGroupState.selected,
        handleDrillDownQueryChange,
    ]);

    useEffect(() => {
        if (!activeDrillDownType) {
            return;
        }

        scrollDashboardToTop();
    }, [activeDrillDownType]);

    const [showPdfTooltip, setShowPdfTooltip] = useState(false);

    const tierLabelById = {
        1: 'Base',
        2: 'Bronze',
        3: 'Silver',
        4: 'Gold',
    };

    const tierLabel = tierLabelById[tenantTierId] ?? 'Unknown';

    return (
        <div
            className="page-content"
            ref={pageContentRef}
            style={{
                height: '100%',
                overflowY: 'auto',
                backgroundColor: activeDrillDownType ? '#fff' : undefined,
            }}
        >
            <div
                className={dashboardPageClassName}
                data-dashboard-export-root="true"
            >
                <div className="dashboard-header-container">
                    <div className="dashboard-header-top">
                        <div className="dashboard-header-left">
                            {!activeDrillDownType && (
                                <>
                                    <div
                                        className="client-name"
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            lineHeight: 1,
                                            verticalAlign: 'middle',
                                        }}
                                    >
                                        {clientName}
                                    </div>

                                    <div
                                        className="client-code"
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            lineHeight: 1,
                                            verticalAlign: 'middle',
                                        }}
                                    >
                                        <span
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                            }}
                                        >
                                            {companyCode
                                                ? `(${companyCode})`
                                                : ''}
                                        </span>
                                        {tenantTierId != null && (
                                            <span
                                                style={{
                                                    marginLeft: 12,
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                }}
                                                title={tierLabel}
                                            >
                                                <TenantTierBadge
                                                    tenantTierId={tenantTierId}
                                                    size={28}
                                                    showLabel={false}
                                                />
                                            </span>
                                        )}
                                    </div>
                                </>
                            )}
                            {activeDrillDownType && (
                                <div className="drill-down-header-title-left">
                                    <div className="drill-down-title-text">
                                        {
                                            getDrillDownLabels(
                                                activeDrillDownType,
                                            ).title
                                        }
                                    </div>
                                    <div className="drill-down-subtitle-text">
                                        {
                                            getDrillDownLabels(
                                                activeDrillDownType,
                                            ).subtitle
                                        }
                                    </div>
                                </div>
                            )}
                        </div>
                        <div
                            className={`dashboard-main-download-actions${
                                activeDrillDownType ? ' is-drill-down' : ''
                            }`}
                            ref={downloadMenuRef}
                        >
                            {activeDrillDownType && (
                                <div className="dashboard-drill-down-top-actions">
                                    <button
                                        className="dashboard-drill-down-clear-filters ing-text-buttons"
                                        onClick={handleClearDrillDownFilters}
                                        type="button"
                                    >
                                        <i className="bi bi-arrow-counterclockwise me-2" />
                                        {getText('msg_381c4bf1d409')}
                                    </button>
                                    <button
                                        className="dashboard-drill-down-header-close"
                                        onClick={handleCloseDrillDown}
                                        type="button"
                                    >
                                        <i className="bi bi-x-lg" />
                                        {getText('msg_bbfa773e5a63')}
                                    </button>
                                </div>
                            )}
                            {hasViewerDataFilter && (
                                <span className="dashboard-header-audience-badge">
                                    <AudienceBadge
                                        label={viewerAudienceLabel}
                                    />
                                </span>
                            )}
                            <button
                                className="ing-button-primary-left align-self-baseline"
                                onClick={
                                    !!activeDrillDownType
                                        ? handleDownloadAllDashboardCsv
                                        : handleToggleDownloadMenu
                                }
                                aria-busy={isExportingReport}
                                type="button"
                            >
                                {getText('msg_a479c9c34e87')}
                            </button>
                            {isDownloadMenuOpen && (
                                <div className="dashboard-main-download-menu">
                                    <button
                                        className="dashboard-main-download-menu-item"
                                        onClick={handleDownloadAllDashboardCsv}
                                        type="button"
                                    >
                                        {getText('msg_3281188351b8')}
                                    </button>
                                    <button
                                        className="dashboard-main-download-menu-item"
                                        onClick={handleDownloadPdfReport}
                                        type="button"
                                    >
                                        {getText('msg_d613d88cb2d8')}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="date-picker-container">
                        <TextRangeDatePicker
                            startDate={
                                activeDrillDownType
                                    ? drillDownStartDate
                                    : startDate
                            }
                            endDate={
                                activeDrillDownType ? drillDownEndDate : endDate
                            }
                            setStartDate={
                                activeDrillDownType
                                    ? setDrillDownStartDate
                                    : setStartDate
                            }
                            setEndDate={
                                activeDrillDownType
                                    ? setDrillDownEndDate
                                    : setEndDate
                            }
                            hideDropdownIndicators={isExportingReport}
                        />
                        <div
                            className="view-report-definitions"
                            onClick={() => setIsDrawerOpen(true)}
                        >
                            {getText('msg_83db64acf280')}
                        </div>
                    </div>
                    <div className="population-groups">
                        <div className="dropdown-population-groups-container">
                            {isExportingReport ? (
                                <div className="population-groups-label">
                                    {getText('msg_a8555d017bee')}
                                </div>
                            ) : (
                                <SearchableDropdown
                                    placeholderLabel={getText('msg_e744dd6b3c48')}
                                    options={activePopulationGroupState.groups}
                                    optionIDField="id"
                                    savedOptionIDField="id"
                                    updateOptions={updatePopulationGroups}
                                    savedOptions={
                                        activePopulationGroupState.selected
                                    }
                                    showTags={false}
                                    minSelections={1}
                                    controlledSelection
                                    exclusiveOptionId={
                                        activePopulationGroupState.groups.find(
                                            g => g.name === 'All Individuals',
                                        )?.id
                                    }
                                />
                            )}
                        </div>
                        <div className="population-group-chips">
                            {activePopulationGroupState.selected
                                .slice(
                                    0,
                                    showAllActivePopulationGroups
                                        ? activePopulationGroupState.selected
                                              .length
                                        : DASHBOARD_POPULATION_GROUPS_MAX,
                                )
                                .map(group => (
                                    <span
                                        key={group.id}
                                        className="badge-base badge-population-group population-group-chip"
                                        style={{
                                            height: '23px',
                                            paddingTop: '3px',
                                        }}
                                    >
                                        {activePopulationGroupState.selected
                                            .length > 1 &&
                                            !isExportingReport && (
                                                <i
                                                    className="bi bi-x-lg"
                                                    style={{
                                                        paddingRight: '6px',
                                                        cursor: 'pointer',
                                                    }}
                                                    onClick={() =>
                                                        handleRemovePopulationGroup(
                                                            group,
                                                        )
                                                    }
                                                />
                                            )}
                                        {group.name}
                                    </span>
                                ))}
                            {activePopulationGroupState.selected.length >
                                DASHBOARD_POPULATION_GROUPS_MAX &&
                                !showAllActivePopulationGroups && (
                                    <div
                                        className="show-more"
                                        onClick={() => {
                                            if (activeDrillDownType) {
                                                setShowAllDrillDownPopulationGroups(
                                                    true,
                                                );
                                                return;
                                            }

                                            setShowAllPopulationGroups(true);
                                        }}
                                    >
                                        +
                                        {activePopulationGroupState.selected
                                            .length -
                                            DASHBOARD_POPULATION_GROUPS_MAX}{' '}
                                        {getText('msg_4fd94bf46ca1')}
                                    </div>
                                )}
                        </div>
                    </div>
                </div>
                <div className="dashboard-content-area">
                    {activeDrillDownType && (
                        <div className="dashboard-card-container dashboard-drill-down-container">
                            <DashboardDrillDownView
                                key={`${activeDrillDownType}-${drillDownResetVersion}`}
                                dashboardTypeId={activeDrillDownType}
                                dashboardData={drillDownData}
                                endDate={drillDownEndDate}
                                onQueryChange={handleDrillDownQueryChange}
                                isLoading={isDrillDownLoading}
                                onRowsForExportChange={
                                    handleDrillDownRowsForExportChange
                                }
                            />
                        </div>
                    )}
                    {!activeDrillDownType && (
                        <>
                            <div className="dashboard-card-container">
                                <CardContainer
                                    title={getText('msg_b233e775d63b')}
                                    onDownloadCsv={() =>
                                        handleDownloadDashboardCardCsv(
                                            DashboardTypeEnum.Registration,
                                        )
                                    }
                                    toolTipText={
                                        DASHBOARD_TOOLTIP_MESSAGE_TEXT[
                                            DashboardTypeEnum.Registration
                                        ]
                                    }
                                >
                                    {loadingState[
                                        DashboardTypeEnum.Registration
                                    ] ? (
                                        <Spinner
                                            width={
                                                DASHBOARD_DEFAULT_SPINNER_WIDTH
                                            }
                                            className="dashboard-loading-spinner"
                                        />
                                    ) : (
                                        <Registration
                                            registrationData={
                                                dashboardData[
                                                    DashboardTypeEnum
                                                        .Registration
                                                ]
                                            }
                                            endDate={endDate}
                                        />
                                    )}
                                </CardContainer>
                                <CardContainer
                                    title={getText('msg_9845e4c7cbf9')}
                                    onDownloadCsv={() =>
                                        handleDownloadDashboardCardCsv(
                                            DashboardTypeEnum.EmploymentStatus,
                                        )
                                    }
                                    toolTipText={
                                        DASHBOARD_TOOLTIP_MESSAGE_TEXT[
                                            DashboardTypeEnum.EmploymentStatus
                                        ]
                                    }
                                >
                                    {loadingState[
                                        DashboardTypeEnum.EmploymentStatus
                                    ] ? (
                                        <Spinner
                                            width={
                                                DASHBOARD_DEFAULT_SPINNER_WIDTH
                                            }
                                            className="dashboard-loading-spinner"
                                        />
                                    ) : (
                                        <EmploymentStatus
                                            employmentStatusData={
                                                dashboardData[
                                                    DashboardTypeEnum
                                                        .EmploymentStatus
                                                ]
                                            }
                                            endDate={endDate}
                                        />
                                    )}
                                </CardContainer>
                            </div>
                            <div className="dashboard-card-container">
                                <CardContainer
                                    title={getText('msg_0179bc349141')}
                                    onDownloadCsv={() =>
                                        handleDownloadDashboardCardCsv(
                                            DashboardTypeEnum.SessionsByDevice,
                                        )
                                    }
                                    toolTipText={
                                        DASHBOARD_TOOLTIP_MESSAGE_TEXT[
                                            DashboardTypeEnum.SessionsByDevice
                                        ]
                                    }
                                >
                                    {loadingState[
                                        DashboardTypeEnum.SessionsByDevice
                                    ] ? (
                                        <Spinner
                                            width={
                                                DASHBOARD_DEFAULT_SPINNER_WIDTH
                                            }
                                            className="dashboard-loading-spinner"
                                        />
                                    ) : (
                                        <SessionsByDevice
                                            sessionsByDeviceData={
                                                dashboardData[
                                                    DashboardTypeEnum
                                                        .SessionsByDevice
                                                ]
                                            }
                                        />
                                    )}
                                </CardContainer>
                                <CardContainer
                                    title={getText('msg_a12fd0292dce')}
                                    onDownloadCsv={() =>
                                        handleDownloadDashboardCardCsv(
                                            DashboardTypeEnum.SessionsDaily,
                                        )
                                    }
                                    toolTipText={
                                        DASHBOARD_TOOLTIP_MESSAGE_TEXT[
                                            DashboardTypeEnum.SessionsDaily
                                        ]
                                    }
                                >
                                    {loadingState[
                                        DashboardTypeEnum.SessionsDaily
                                    ] ? (
                                        <Spinner
                                            width={
                                                DASHBOARD_DEFAULT_SPINNER_WIDTH
                                            }
                                            className="dashboard-loading-spinner"
                                        />
                                    ) : (
                                        <SessionsDaily
                                            sessionsDailyData={
                                                dashboardData[
                                                    DashboardTypeEnum
                                                        .SessionsDaily
                                                ]
                                            }
                                            startDate={startDate}
                                            endDate={endDate}
                                        />
                                    )}
                                </CardContainer>
                            </div>
                            <div className="dashboard-card-container">
                                <CardContainer
                                    title={getText('msg_48a28af61b7c')}
                                    onDownloadCsv={() =>
                                        handleDownloadDashboardCardCsv(
                                            DashboardTypeEnum.MostVisitedPages,
                                        )
                                    }
                                    onViewDrillDown={() =>
                                        handleOpenDrillDown(
                                            DashboardTypeEnum.MostVisitedPages,
                                        )
                                    }
                                    toolTipText={
                                        DASHBOARD_TOOLTIP_MESSAGE_TEXT[
                                            DashboardTypeEnum.MostVisitedPages
                                        ]
                                    }
                                >
                                    {loadingState[
                                        DashboardTypeEnum.MostVisitedPages
                                    ] ? (
                                        <Spinner
                                            width={
                                                DASHBOARD_DEFAULT_SPINNER_WIDTH
                                            }
                                            className="dashboard-loading-spinner"
                                        />
                                    ) : (
                                        <MostVisitedPages
                                            mostVisitedPagesData={
                                                dashboardData[
                                                    DashboardTypeEnum
                                                        .MostVisitedPages
                                                ]
                                            }
                                        />
                                    )}
                                </CardContainer>
                                <CardContainer
                                    title={getText('msg_dde98391b037')}
                                    onDownloadCsv={() =>
                                        handleDownloadDashboardCardCsv(
                                            DashboardTypeEnum.MostClickedContent,
                                        )
                                    }
                                    onViewDrillDown={() =>
                                        handleOpenDrillDown(
                                            DashboardTypeEnum.MostClickedContent,
                                        )
                                    }
                                    toolTipText={
                                        DASHBOARD_TOOLTIP_MESSAGE_TEXT[
                                            DashboardTypeEnum.MostClickedContent
                                        ]
                                    }
                                >
                                    {loadingState[
                                        DashboardTypeEnum.MostClickedContent
                                    ] ? (
                                        <Spinner
                                            width={
                                                DASHBOARD_DEFAULT_SPINNER_WIDTH
                                            }
                                            className="dashboard-loading-spinner"
                                        />
                                    ) : (
                                        <MostClickedContent
                                            mostClickedContentData={
                                                dashboardData[
                                                    DashboardTypeEnum
                                                        .MostClickedContent
                                                ]
                                            }
                                        />
                                    )}
                                </CardContainer>
                            </div>
                            <div className="dashboard-card-container">
                                <CardContainer
                                    title={getText('msg_08237d4d18a3')}
                                    onDownloadCsv={() =>
                                        handleDownloadDashboardCardCsv(
                                            DashboardTypeEnum.MessagesUserPreference,
                                        )
                                    }
                                    toolTipText={
                                        DASHBOARD_TOOLTIP_MESSAGE_TEXT[
                                            DashboardTypeEnum
                                                .MessagesUserPreference
                                        ]
                                    }
                                >
                                    {loadingState[
                                        DashboardTypeEnum.MessagesUserPreference
                                    ] ? (
                                        <Spinner
                                            width={
                                                DASHBOARD_DEFAULT_SPINNER_WIDTH
                                            }
                                            className="dashboard-loading-spinner"
                                        />
                                    ) : (
                                        <MessagesUserPreference
                                            messagesUserPreferenceData={
                                                dashboardData[
                                                    DashboardTypeEnum
                                                        .MessagesUserPreference
                                                ]
                                            }
                                            endDate={endDate}
                                        />
                                    )}
                                </CardContainer>
                                <CardContainer
                                    title={getText('msg_959d64669a63')}
                                    onDownloadCsv={() =>
                                        handleDownloadDashboardCardCsv(
                                            DashboardTypeEnum.MessagesSent,
                                        )
                                    }
                                    toolTipText={
                                        DASHBOARD_TOOLTIP_MESSAGE_TEXT[
                                            DashboardTypeEnum.MessagesSent
                                        ]
                                    }
                                >
                                    {loadingState[
                                        DashboardTypeEnum.MessagesSent
                                    ] ? (
                                        <Spinner
                                            width={
                                                DASHBOARD_DEFAULT_SPINNER_WIDTH
                                            }
                                            className="dashboard-loading-spinner"
                                        />
                                    ) : (
                                        <MessagesSent
                                            messagesSentData={
                                                dashboardData[
                                                    DashboardTypeEnum
                                                        .MessagesSent
                                                ]
                                            }
                                            startDate={startDate}
                                            endDate={endDate}
                                        />
                                    )}
                                </CardContainer>
                            </div>
                        </>
                    )}
                </div>
                <div data>
                    <SideDrawer
                        isOpen={isDrawerOpen}
                        setIsOpen={setIsDrawerOpen}
                        header={drawerHeader}
                        sections={drawerSections}
                    />
                </div>
            </div>
            {isExportingReport && (
                <div
                    className="dashboard-export-overlay"
                    role="status"
                    aria-live="polite"
                >
                    <div className="dashboard-export-overlay-content">
                        <div className="dashboard-export-overlay-title">
                            {getText('msg_63499997499d')}
                        </div>
                        <div className="dashboard-export-overlay-subtitle">
                            {getText('msg_d9e3c8dd911e')}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DashboardPage;
